import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { ASSETS } from '../../utils/config';
import { REFERRAL_INVITE_FIX_ENABLED } from '../../constants/featureFlags';

// Pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

// Container for the component
const HoverBetaContainer = styled.div`
  display: inline-block;
  position: relative;
  cursor: pointer;
  width: 90px; /* Ensure container matches the image width */
  height: 40px; /* Ensure container matches the image height */
  margin-right: 10px;
`;

// Styled for the images
const BetaImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute; /* Ensure both images overlap */
  animation: ${pulse} 1.5s infinite;
  transition: opacity 0.5s, transform 0.5s;
`;

// QA-149 (Kelly, 2026-08-19): the gallery header's Invite entry point was a
// purple filled button with a "Share" text label — the only header action
// that looked like a CTA instead of a nav icon, and it didn't match its
// neighbours (Home/Discover/Capture/Messages), which are all plain 40x40
// line-art icon buttons (see WheelGallery/Header/styles.js IconWrapper).
// Kelly's ask: "REMOVE the purple button and the word Share; plain icon,
// same color as other icons." This mirrors IconWrapper's own box/hover
// geometry (40x40, 8px padding, 4px radius, gray hover highlight) so Invite
// reads as one more icon in the row, not a CTA — without importing Header's
// private styles module (EnterBetaButton is also used, unmodified, by
// SearchHeader/Navbar/WheelHeader via the asShareButton=false default path).
//
// ISP-9999 (Gordon 2026-08-19): a follow-up pass tightened this button's
// horizontal padding (14px -> 10px each side) back when it was still the
// purple ShareButtonContainer. QA-149 superseded that entirely -- the purple
// fill, text label, and 8px/14px padding are all gone in favor of this plain
// 40x40 icon box (uniform 8px padding), so there is no separate horizontal
// value left to tighten.
//
// ISP-9999 (#959, Gordon 2026-08-21): swapped the glyph from the generic
// Share icon to the envelope (MailOutline) icon already used for "Invite"
// elsewhere (per #947), so the header's Invite entry point reads as an
// invite, not a share, in both this bar button AND HeaderOverflowMenu's
// phone-fold "Invite Friends" row — one consistent envelope glyph for the
// same action instead of two different icons for it.
const InviteIconButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 40px;
  width: 40px;
  box-sizing: border-box;
  padding: 8px;
  border-radius: 4px;
  margin-right: 10px;
  transition: box-shadow 0.2s ease, background-color 0.2s ease;

  &:active {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    background-color: #dee2e66e;
  }
`;

export const EnterBetaButton = ({
  onClickBeta,
  onlyInvite = false,
  button = ASSETS.InviteBtn,
  ariaLabel = 'Invite friends to iYuga',
  asShareButton = false,
}) => {
  const [hovered, setHovered] = useState(false);

  // EW-1121: when the fix is ON, the container becomes a real focusable control.
  // This is the ONLY entry to the referral/invite flow, and today it is an
  // alt-less <img> in tabIndex=-1 divs with no role/aria -> 0% keyboard/SR
  // reachable. OFF keeps it byte-identical to today (no role/tabIndex/aria/
  // onKeyDown; images carry no alt attribute).
  const a11yProps = REFERRAL_INVITE_FIX_ENABLED
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': ariaLabel,
        onKeyDown: (event) => {
          if (
            event.key === 'Enter' ||
            event.key === ' ' ||
            event.key === 'Spacebar'
          ) {
            event.preventDefault();
            if (typeof onClickBeta === 'function') onClickBeta(event);
          }
        },
      }
    : {};

  // Decorative alt="" when the container carries the accessible name; undefined
  // (React renders no attribute) when OFF so the markup is byte-identical.
  const imgAlt = REFERRAL_INVITE_FIX_ENABLED ? '' : undefined;

  // QA-149 / #959: standard, static Invite icon button — no pulse animation,
  // no purple fill, no text label. The envelope glyph renders at the same
  // PrimaryPurple500 (#9B5AF8) as every neighbouring header icon, inside the
  // same 40x40 hover-highlight box. The accessible name moves from visible
  // text onto aria-label so screen-reader users lose nothing. This is a
  // distinct render path so the other EnterBetaButton call sites (referral
  // Invite CTA in Navbar/WheelHeader, logged-out "Join iYuga" CTA via
  // onlyInvite) are completely unaffected.
  // gordon-0831-header-invite-tooltip-slow: this was the only header icon
  // whose tooltip was a bare native `title` attribute — OS-rendered, on its
  // own multi-second delay, invisible to the DOM — instead of the app's MUI
  // Tooltip that every sibling header icon uses (shows a role="tooltip"
  // popper on MUI's fast default enterDelay). Wrapping in Tooltip and
  // dropping the native `title` gives it the same fast, consistent tooltip
  // as its neighbours instead of a slow/uncontrolled native one.
  if (asShareButton) {
    return (
      <Tooltip title="Invite friends to iYuga">
        <InviteIconButton
          role="button"
          tabIndex={0}
          aria-label="Invite friends to iYuga"
          onClick={onClickBeta}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (typeof onClickBeta === 'function') onClickBeta(event);
            }
          }}
        >
          <MailOutlineIcon style={{ color: '#9B5AF8', fontSize: 26 }} />
        </InviteIconButton>
      </Tooltip>
    );
  }

  return (
    <HoverBetaContainer
      onMouseEnter={() => !onlyInvite && setHovered(true)}
      onMouseLeave={() => !onlyInvite && setHovered(false)}
      onClick={onClickBeta}
      {...a11yProps}
    >
      {/* Image for "Want In" */}
      <BetaImage
        src={button}
        alt={imgAlt}
        style={{
          opacity: hovered ? 0 : 1,
          transform: hovered ? 'translateY(-10px)' : 'translateY(0)',
        }}
      />

      {/* Image for "Enter Beta" */}
      <BetaImage
        src={ASSETS.EnterBeta}
        alt={imgAlt}
        style={{
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translateY(0)' : 'translateY(10px)',
        }}
      />
    </HoverBetaContainer>
  );
};
